import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Center, Stack, Text, Title } from '@mantine/core';

import { findPrimarySample } from 'util/sample';
import { RootState } from 'store';
import { searchSample } from 'store/labSamples/thunks';
import { userIsAdmin, userIsSuperAdmin } from 'store/user/selectors';
import CenteredLoader from 'common/CenteredLoader';

import BulkDensityInputForm from './BulkDensityInputForm';

type BulkDensityDetailsPropsType = {
  barcode?: string;
};

const BulkDensityDetails = ({ barcode }: BulkDensityDetailsPropsType) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { samples, hasFetched, hasFailed } = useSelector((state: RootState) => ({
    samples: barcode ? state.labSamples.byBarcode[barcode] : [],
    hasFetched: state.labSamples.hasFetched,
    hasFailed: state.labSamples.hasFailed,
    isAdmin: userIsAdmin(state) || userIsSuperAdmin(state),
  }));

  useEffect(() => {
    if (barcode && !samples && !hasFailed) {
      dispatch(searchSample(barcode));
    }
  }, [dispatch, samples, hasFailed, barcode]);

  const primarySample = samples?.length ? findPrimarySample(samples) || samples[0] : null;

  let content: React.ReactNode = null;

  if (!hasFetched && !samples?.length) {
    content = <CenteredLoader />;
  }

  if (!primarySample) {
    content = (
      <>
        <Title order={2}>{t('sampleNotFound')}</Title>
        <Text>{t('sampleNotFoundMsg')}</Text>
      </>
    );
  }

  if (content) {
    return (
      <Center>
        <Stack align="center">{content}</Stack>
      </Center>
    );
  }

  return <BulkDensityInputForm sample={primarySample} />;
};

export default BulkDensityDetails;
